import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useProducts } from "../context/ProductContext";
import ProductCard from "../components/ProductCard";
import AccountDetailsPopup from "../components/AccountDetailsPopup";

const Home = ({ searchTerm, onClearSearch }) => {
  const [showAccountPopup, setShowAccountPopup] = useState(false);
  const { products, categories, isLoading } = useProducts();

  const filteredProducts = searchTerm
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.description.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : products;

  const handleWhatsAppClick = (product, variant, quantity) => {
    // Check if account details exist
    const accountDetails = localStorage.getItem("accountDetails");
    if (!accountDetails) {
      setShowAccountPopup(true);
      return;
    }

    const account = JSON.parse(accountDetails);
    const qty = quantity > 0 ? quantity : 1;
    const totalPrice = variant.offerPrice * qty;

    let message = `🛒 *NEW ORDER REQUEST*\n\n`;
    message += `📦 *Product Details:*\n`;
    message += `─────────────────\n`;
    message += `${product.name}\n`;
    message += `📏 Size: ${variant.size}\n`;
    message += `🔢 Quantity: ${qty}\n`;
    message += `💰 Price per unit: ₹${variant.offerPrice}\n`;
    message += `─────────────────\n`;
    message += `💵 *Total: ₹${totalPrice}*\n\n`;

    message += `📍 *Delivery Details:*\n`;
    message += `─────────────────\n`;
    message += `👤 Name: ${account.name || "N/A"}\n`;
    message += `📱 Mobile: ${account.mobile}\n`;
    message += `📧 Email: ${account.email || "N/A"}\n`;
    message += `🏠 Address:\n`;
    message += `   ${account.addressLine1}\n`;
    if (account.addressLine2) message += `   ${account.addressLine2}\n`;
    if (account.addressLine3) message += `   ${account.addressLine3}\n`;
    message += `   ${account.city}, ${account.state} - ${account.pincode}\n\n`;
    message += `*Additional shipping charges applicable.*\n`;

    const whatsappUrl = `https://wa.me/919840453354?text=${encodeURIComponent(
      message,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="text-gray-600 mt-3">Loading products...</p>
          </div>
        </div>
      )}

      {!searchTerm && (
        <>
          {/* Banner Section */}
          <div className="bg-gradient-to-r from-green-50 to-red-50 py-4 px-4 mb-8">
            <div className="max-w-7xl mx-auto">
              {/* Banner Details */}
              <div className="mb-6">
                <img
                  src="/banner.png"
                  alt="Special Offers Banner"
                  className="w-full h-auto rounded-xl shadow-md"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Category Products Sections / Search Results */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {searchTerm ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-800">
                Search Results for "{searchTerm}"
              </h2>
              <button
                onClick={onClearSearch}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear Search
              </button>
            </div>

            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    showCategory={true}
                    onWhatsAppClick={handleWhatsAppClick}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">No products found</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Overall Categories Header */}
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800">
                Our Products
              </h2>
              <Link
                to="/products"
                className="bg-green-500 text-white px-3 py-2 md:px-6 md:py-3 rounded-lg hover:bg-green-600 font-semibold transition-colors flex items-center gap-2 group text-sm md:text-base"
              >
                <span className="hidden sm:inline">View All Products</span>
                <span className="sm:hidden">View All</span>
                <span className="group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </Link>
            </div>

            {/* Category-based Product Sections */}
            {categories.map((category) => {
              const categoryProducts = products
                .filter((p) => p.category === category.id)
                .slice(0, 4);

              if (categoryProducts.length === 0) return null;

              return (
                <div key={category.id} className="mb-16">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                      <span className="text-3xl md:text-4xl">
                        {category.icon}
                      </span>
                      {category.name}
                    </h2>
                    <Link
                      to="/products"
                      state={{ category: category.id }}
                      className="text-green-600 hover:text-green-700 font-semibold flex items-center gap-2 group"
                    >
                      View All
                      <span className="group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {categoryProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        showCategory={false}
                        onWhatsAppClick={handleWhatsAppClick}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <AccountDetailsPopup
        isOpen={showAccountPopup}
        onClose={() => setShowAccountPopup(false)}
      />
    </div>
  );
};

export default Home;
